import * as Mui from  "@mui/material";
import FormInput from "../../../common/FormInput";
import CloseIcon from '@mui/icons-material/Close';
import FileUploader from "../../../../../../util/component/FileUploader";
import { useContext, useEffect, useMemo, useState } from "react";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import { AutoCompleteOption } from "../../../common/AutoComplete";
import DamageFormService from "../../../damageFormList/service/DamageFormService";
import { ContextOperation } from "../../../../context/OperationContext";
import { useForm } from "react-hook-form";
import { CreateDamageFormTypes } from "../../../damageFormList/Types";
import { nameof } from "../../../common/ObjectUtils";
import { useLocation } from "react-router-dom";
import OperationTypes from "../../../../types";
import useSnackbar from "../../../../hooks/useSnackbar";
import DamagePriceService from "../../service/damagePrice.action";

interface EditDamageForm {
    partId: number; // ID della parte selezionata
    positionId: number; // ID della posizione della parte selezionata
    severity: string; // Severity selezionata
}

interface Props {
    open: boolean;
    onClose: () => void;
    parts: OperationTypes.VehiclePart[];
    severities: string[];
    damage?: OperationTypes.Damage;
}
const EditDamage = (props: Props) => {
    const t = useOperationTranslation();
    const { tenant, setLoading } = useContext(ContextOperation);

    const snackbar = useSnackbar();

    const locationState = useLocation().state as CreateDamageFormTypes.ISharedLocationState;

    const form = useForm<EditDamageForm>();

    const parts = useMemo(() => {
      return props.parts.map((part) => ({
          label: part.label,
          value: `${part.part.id}.${part.position.id}`,
      }));
  }, [props.parts]);

  const severities = useMemo(() => {
      return props.severities.map((sev) => ({
          label: sev,
          value: sev
      }));
  }, [props.severities]);

    useEffect(() => {
      if (!props.open) {
        return;
      }
      
      if (!props.damage) {
        snackbar.show("warning",  t("damageList.damageCreation.message.selectedDamageNotDefined"));
        return;
      }

      form.reset({
        partId: props.damage.part.id,
        positionId: props.damage.position.id,
        severity: props.damage.severity,
      });

      DamageFormService.create.get.photos(props.damage.id.toString()).then(
        response => {
            setFileUploadedOne(new File([], response[0].filename));
            setFileUploadedTwo(new File([], response[1].filename));
        }
      )
    }, [props.damage]);

    const [fileUploadedOne, setFileUploadedOne] = useState<File | null>(null);
    const [fileUploadedTwo, setFileUploadedTwo] = useState<File | null>(null);

    const onFileUpload = (file: File, photoNumber: number) => {
        if (photoNumber === 1) {
            setFileUploadedOne(file);
        } else if (photoNumber === 2) {
            setFileUploadedTwo(file);
        }
    }

    const onSubmit = (data: EditDamageForm) => {
      if (!props.damage) {
        return;
      }

      if ((fileUploadedOne !== null && !props.damage?.firstSnapshotId) ||
         (fileUploadedTwo !== null && !props.damage?.secondSnapshotId)) {
            setLoading(true);

            DamagePriceService.post.addPhoto(props?.damage?.id, [fileUploadedOne, fileUploadedTwo]).then(
                responsePhoto => {
                    if (props.damage != null) {
                        if (fileUploadedOne) {
                                    
                            props.damage.firstSnapshotId = responsePhoto[0].id;

                            if (fileUploadedTwo) {
                                props.damage.secondSnapshotId = responsePhoto[1].id;
                            }

                        } else {
                            if (fileUploadedTwo) {
                                props.damage.secondSnapshotId = responsePhoto[0].id;
                            }
                        }
                    }

                    setLoading(false);
                    onClose();
                },
                error => {
                    setLoading(false);              
                    onClose();
                    snackbar.show("error", t("damageList.damageCreation.message.uploadDamageSnapshotErrorMessage"));      
                }
            )
         } else {
            onClose();
         }
    }

    const onClose = () => {
        form.reset();
        props.onClose();
    }

    const onRemoveFile = (idAttachment: number | null | undefined, photo: number) => {
        if (idAttachment) {
            DamagePriceService.delete.attachment(tenant, idAttachment.toString(), 'SNAPSHOT').then(
                responsePhoto => {

                    if (props.damage !== null && props.damage !== undefined) {
                        if (photo === 1) {
                            props.damage.firstSnapshotId = null;
                            setFileUploadedOne(null);
                        } else if (photo === 2) {
                            props.damage.secondSnapshotId = null;
                            setFileUploadedTwo(null);
                        }
                    }
                },
                error => {
                    snackbar.show("error", t("damageList.damageCreation.message.deleteDamageSnapshotErrorMessage"));
                }
            )   
        }
    }
    
    return(
        <Mui.Dialog
            open={props.open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <Mui.DialogTitle>
                <Mui.Grid container alignItems={"center"}>
                    <Mui.Grid item>
                        <Mui.Typography variant="h5">
                            {t("damageList.damageCreation.dialogs.createDamage.title")}
                        </Mui.Typography>
                    </Mui.Grid>
                    <Mui.Grid item marginLeft={"auto"}>
                        <Mui.IconButton onClick={onClose}>
                            <CloseIcon />
                        </Mui.IconButton>
                    </Mui.Grid>
                </Mui.Grid>
            </Mui.DialogTitle>
            
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <Mui.DialogContent>
                    <Mui.Grid container spacing={3} mb={3}>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<EditDamageForm>("partId")}
                                type="select"
                                options={parts}
                                fullWidth
                                label={t("damageList.damageCreation.dialogs.createDamage.vehiclePart")}
                                value={`${form.watch("partId")}.${form.watch("positionId")}`}
                                onChange={(val) => {
                                    form.setValue("partId", parseInt(val?.split(".")?.[0]));
                                    form.setValue("positionId", parseInt(val?.split(".")?.[1]));
                                }}
                                disabled={true}
                            />
                        </Mui.Grid>
                        <Mui.Grid item xs={12} sm={6} md={6}>
                            <FormInput
                                name={nameof<EditDamageForm>("severity")}
                                type="select"
                                options={severities}
                                formInstance={form}
                                fullWidth
                                label={t("damageList.damageCreation.dialogs.createDamage.severity")}
                                disabled={true}
                            />
                        </Mui.Grid>
                    </Mui.Grid>

                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 1)}
                        onRemove={() => onRemoveFile(props.damage?.firstSnapshotId, 1)}
                        uploadedFiles={fileUploadedOne ? [fileUploadedOne] : []}
                    />
                    <FileUploader 
                        onUpload={(file: File) => onFileUpload(file, 2)}
                        onRemove={() => onRemoveFile(props.damage?.secondSnapshotId, 2)}
                        uploadedFiles={fileUploadedTwo ? [fileUploadedTwo] : []}
                    />
                </Mui.DialogContent>

                <Mui.DialogActions>
                    <Mui.Button variant="outlined" size="smallPlus" onClick={onClose}>
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageList.damageCreation.button.cancel")}
                        </Mui.Typography>
                    </Mui.Button>
                    <Mui.Button type="submit" size="smallPlus">
                        <Mui.Typography variant="h6" color="inherit">
                            {t("damageList.damageCreation.button.save")}
                        </Mui.Typography>
                    </Mui.Button>
                </Mui.DialogActions>
            </form>

        </Mui.Dialog>
    )
}
export default EditDamage;